import {
  hasAdminScheduledFutureDrop,
  isScheduledDropPending,
  normalizeEnrollmentStatus,
} from "@/lib/utils/enrollment";
import {
  EnrollmentStatus,
  type GetEnrollmentResponse,
} from "@/openapi/generated";

export type EnrollmentStatusDisplayTone =
  | "success"
  | "premium"
  | "info"
  | "neutral"
  | "danger";

/** Staff/parent label for API enrollment status. `Awaiting` is waitlist. */
export function getEnrollmentStatusDisplayLabel(status: unknown): string {
  const normalized = normalizeEnrollmentStatus(status);
  switch (normalized) {
    case EnrollmentStatus.Enrolled:
      return "Enrolled";
    case EnrollmentStatus.Pending:
      return "Pending review";
    case EnrollmentStatus.Awaiting:
      return "Waitlist";
    case EnrollmentStatus.ScheduledDrop:
      return "Scheduled drop";
    case EnrollmentStatus.Dropped:
      return "Dropped";
    case EnrollmentStatus.Completed:
      return "Completed";
    case EnrollmentStatus.Cancelled:
      return "Cancelled";
    case EnrollmentStatus.Rejected:
      return "Rejected";
    default:
      return status == null || status === "" ? "" : String(status);
  }
}

export function getEnrollmentStatusDisplayTone(
  status: unknown,
): EnrollmentStatusDisplayTone {
  switch (normalizeEnrollmentStatus(status)) {
    case EnrollmentStatus.Enrolled:
      return "success";
    case EnrollmentStatus.Pending:
      return "premium";
    case EnrollmentStatus.Awaiting:
    case EnrollmentStatus.ScheduledDrop:
      return "info";
    case EnrollmentStatus.Dropped:
    case EnrollmentStatus.Cancelled:
    case EnrollmentStatus.Rejected:
      return "danger";
    default:
      return "neutral";
  }
}

/** Badge copy for a live enrollment row, including scheduled withdraw overrides. */
export function getEnrollmentStatusRowLabel(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): string {
  if (hasAdminScheduledFutureDrop(enrollment)) {
    return "Withdrawal scheduled";
  }
  if (isScheduledDropPending(enrollment)) {
    return "Withdrawal requested";
  }
  return getEnrollmentStatusDisplayLabel(enrollment.status);
}

export function getEnrollmentStatusRowTone(
  enrollment: Pick<GetEnrollmentResponse, "status" | "droppedAt">,
): EnrollmentStatusDisplayTone {
  if (
    hasAdminScheduledFutureDrop(enrollment) ||
    isScheduledDropPending(enrollment)
  ) {
    return "info";
  }
  const tone = getEnrollmentStatusDisplayTone(enrollment.status);
  return tone === "danger" ? "premium" : tone;
}
